import { Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL, postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import Spinner from 'src/utils/Loader/spinner';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import { useToaster } from 'src/utils/toaster/toasterContext';
import CommonReAuthenticateComponent from '../integration/commonReAuthenticate';
import MobileNewPostComponent from './mobile-new-post.component';
import PostContentComponent from './post-content.component';
import PostFooter from './post-footer.component';
import PostTabComponent from './post-tab.component';
import { PostUtil } from './post-util';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const OrgNewPostComponent = ({isShowOfferBanner=false}) => {
  const currentbrandTime = CommonHelper.getBrandTimeZoneTime();
  const PostOption = {
    PostNow: { Name: 'postnow', Label: 'Post Now' },
    SchedulePost: { Name: 'schedulepost', Label: 'Scheduled Post' },
    ApprovalPost: { Name: 'approval', Label: 'Send for Approval' },
  };

  const OrgNewPostStruct = {
    media_url: '',
    media_data: [],
    description: '',
    scheduled_time: currentbrandTime,
    is_file_upload: false,
    connected_profile_ids: [],
    scheduled_event_requests_data: {
      facebook_requests_data: [],
      instagram_requests_data: [],
      twitter_requests_data: [],
      threads_requests_data: [],
      youtube_requests_data: [],
      gbusiness_requests_data: [],
      linkedin_requests_data: [],
      tiktok_requests_data: [],
      pinterest_requests_data: [],
      bluesky_requests_data: [],
      snapchat_requests_data: [],
    },
    brand_details: [], // Changed to array of brand objects
    comment_description: '',
    has_comment: false,
  };

  const location = useLocation();
  const { showToast } = useToaster();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasApproverPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.APPROVER);
  const orgDetails = CommonHelper.GetBrandInfoFromLocalStorage();
  const { checkAndTriggerReAuth } = useReAuth();

  // State Variables
  const [loader, setLoader] = useState(true);
  const [configuredSocialMedia, setConfiguredSocialMedia] = useState([]);
  const [selectedSocialMedias, setSelectedSocialMedias] = useState([]);
  const [scheduleDateTime, setScheduleDateTime] = useState(currentbrandTime);
  const [scheduleId, setScheduleId] = useState(null);
  const [isSubmitting, setPostSubmitting] = useState(false);
  const [isSubmittingDraft, setDraftSubmitting] = useState(false);
  const [whenToPost, setWhenToPost] = useState(
    hasApproverPermission ? PostOption.PostNow.Name : PostOption.ApprovalPost.Name
  );
  const [newPostForm, setNewPostForm] = useState(OrgNewPostStruct);
  const [tabValue, setTabValue] = useState(1);
  const [hashTags, setHashTags] = useState('');
  const [selectedPopOverAccount, setSelectedPopOverAccount] = useState();
  const [reAuthenticateOpen, setReAuthenticateOpen] = useState(false);

  // Ref Variables
  const facebookConditionRef = useRef(null);
  const instagramConditionRef = useRef(null);
  const twitterConditionRef = useRef(null);
  const threadsConditionRef = useRef(null);
  const youtubeConditionRef = useRef(null);
  const linkedInConditionRef = useRef(null);
  const tiktokConditionRef = useRef(null);
  const googleMyBusinessConditionRef = useRef(null);
  const pinterestConditionRef = useRef(null);
  const blueskyConditionRef = useRef(null);
  const snapchatConditionRef = useRef(null);
  const previewRef = useRef(null);

  const setStateToInitialStage = () => {
    setSelectedSocialMedias([]);
    setScheduleDateTime(currentbrandTime);
    setWhenToPost(hasApproverPermission ? PostOption.PostNow.Name : PostOption.ApprovalPost.Name);
    setNewPostForm(OrgNewPostStruct);
    setHashTags('');

    facebookConditionRef?.current?.handleRequestData([]);
    instagramConditionRef?.current?.handleRequestData([]);
    twitterConditionRef?.current?.handleRequestData([]);
    threadsConditionRef?.current?.handleRequestData([]);
    youtubeConditionRef?.current?.handleRequestData([]);
    tiktokConditionRef?.current?.handleRequestData([]);
    linkedInConditionRef?.current?.handleRequestData([]);
    googleMyBusinessConditionRef?.current?.handleRequestData([]);
    pinterestConditionRef?.current?.handleRequestData([]);
    blueskyConditionRef?.current?.handleRequestData([]);
    snapchatConditionRef?.current?.handleRequestData([]);
    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(OrgNewPostStruct)));
  };

  useEffect(() => {
    getOrgConnectedNetworks();
  }, []);

  const getOrgConnectedNetworks = () => {
    let getProps = {
      url: integrateModuleURL.getOrgConnectedNetworks,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const connectedNetworksSuccessCallback = (data, message) => {
    setLoader(false);
    message && showToast(message, 'e');
    const filterConnectedNetworks = data?.filter((a) => a.is_enabled);
    const filterActiveConnectedNetworks = data?.filter((a) => a.is_enabled && a.is_active);
    setConfiguredSocialMedia(filterConnectedNetworks);
    //TODO: Need to check the plan and need to select
    if (location.state?.data && location.state?.data?.is_org_level_post) {
      const postState = location.state?.data;
      const scheduledTime = CommonHelper.getBrandTimeZoneTime(postState.scheduled_time);
      const currentTime = currentbrandTime;

      setWhenToPost(scheduledTime > currentTime ? PostOption.SchedulePost.Name : PostOption.PostNow.Name);
      setScheduleId(postState.schedule_id);
      setScheduleDateTime(scheduledTime > currentTime ? scheduledTime : currentTime);
      if (postState.connected_profile_ids?.length > 0) {
        const selectedAccounts = filterActiveConnectedNetworks.filter((a) =>
          postState.connected_profile_ids.includes(a.id)
        );

        const form = { ...postState };
        const updatedBrands = [];
        const activeServices = new Set();

        // Process brand details and collect active services
        form.brand_details?.forEach((brandDetail) => {
          const connected_profiles_by_service = brandDetail.connected_profiles_by_service;
          const newBrand = { ...brandDetail, connected_profiles_by_service: {} };

          Object.keys(connected_profiles_by_service).forEach((service) => {
            // Filter profiles to only include selected active accounts
            newBrand.connected_profiles_by_service[service] = connected_profiles_by_service[service].filter((profile) =>
              selectedAccounts.some((acc) => acc.id === profile.connected_profile_id)
            );

            // Track services with active profiles
            if (newBrand.connected_profiles_by_service[service].length > 0) {
              activeServices.add(service);
            }
          });
          updatedBrands.push(newBrand);
        });

        form.brand_details = updatedBrands;

        // Clear scheduled event requests for services with no active profiles
        Object.keys(form.scheduled_event_requests_data).forEach((key) => {
          const service = key.replace('_requests_data', '');
          if (!activeServices.has(service)) {
            form.scheduled_event_requests_data[key] = [];
          }
        });

        setNewPostForm(form);
        setSelectedSocialMedias(selectedAccounts);
        updateRefs(form); // Update refs with the form data
      }
    } else if (location.state?.media_data && location.state?.data?.is_org_level_post) {
      setSelectedSocialMedias(filterActiveConnectedNetworks);
      processConnectedNetworksData(filterActiveConnectedNetworks);
      let mediaData = location.state.media_data;
      handleMediaFiles(mediaData.media_data);
    } else {
      setSelectedSocialMedias(filterActiveConnectedNetworks);
      processConnectedNetworksData(filterActiveConnectedNetworks);
    }
    checkAndTriggerReAuth(data);
  };

  const failureCallback = (message) => {
    setLoader(false);
    setDraftSubmitting(false);
    setPostSubmitting(false);
    message && showToast(message, 'e');
  };

  const getCommonRequestData = (network, formData) => {
    return {
      connected_profile_id: 0,
      media_url: formData.media_url,
      media_data: formData.media_data,
      description: formData.description,
      comment_description: formData.comment_description,
      has_comment: formData.comment_description !== '' ? true : false,
      connected_profile_details: {
        connected_profile_id: 0,
        connected_profile_name: orgDetails?.org_name,
        connected_profile_image: orgDetails?.org_name,
        social_media: network.service_name,
      },
    };
  };

  const processConnectedNetworksData = (connectedNetworks) => {
    let formData = { ...OrgNewPostStruct };
    // Preserve user-entered data
    formData.description = newPostForm.description;
    formData.media_data = newPostForm.media_data;
    formData.media_url = newPostForm.media_url;
    formData.comment_description = newPostForm.comment_description;
    formData.has_comment = newPostForm.comment_description !== '' ? true : false;
    formData.scheduled_time = newPostForm.scheduled_time;

    // Group networks by brand_id
    const brandsMap = {};

    connectedNetworks.forEach((network) => {
      formData.connected_profile_ids = formData.connected_profile_ids.concat(network.id);

      // Initialize brand if not exists
      if (!brandsMap[network.brand_id]) {
        brandsMap[network.brand_id] = {
          brand_id: network.brand_id,
          connected_profiles_by_service: {
            facebook: [],
            instagram: [],
            twitter: [],
            threads: [],
            youtube: [],
            linkedin: [],
            tiktok: [],
            gbusiness: [],
            pinterest: [],
            bluesky: [],
            snapchat: [],
          },
        };
      }

      // Add profile to brand
      const brand = brandsMap[network.brand_id];
      brand.connected_profiles_by_service[network.service_name.toLowerCase()].push({
        connected_profile_id: network.id,
        connected_profile_name: network.profile_name,
        connected_profile_image: network.profile_image,
        social_media: network.service_name,
      });

      const commonData = getCommonRequestData(network, formData);
      const serviceName = network.service_name.toUpperCase();
      const serviceKey = network.service_name.toLowerCase() + '_requests_data';

      // Check if a default post type already exists for this service
      const existingRequest = formData.scheduled_event_requests_data[serviceKey].find(
        (req) => req.post_type === PostUtil.getDefaultPostType(serviceName)
      );

      if (!existingRequest) {
        switch (serviceName) {
          case PostUtil.ServiceName.FACEBOOK:
            formData.scheduled_event_requests_data.facebook_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.POST,
              link: '',
              thumbnail: '',
              is_automation_trigger: false,
              automation_trigger: {
                trigger_type: '',
                trigger_values: [],
                any_trigger_value: false,
                dm_message: '',
                comment_reply: '',
                is_dm_template: false,
                dm_template: {
                  title: '',
                  subtitle: '',
                  image_url: '',
                  default_action: { type: 'web_url', url: '' },
                  buttons: [],
                },
              },
              connected_profile_details: {
                ...commonData.connected_profile_details,
                webhook_profile_id: network.webhook_profile_id,
              },
            });
            break;
          case PostUtil.ServiceName.INSTAGRAM:
            formData.scheduled_event_requests_data.instagram_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.POST,
              cover_url: '',
              post_on_my_wall: true,
              is_automation_trigger: false,
              automation_trigger: {
                trigger_type: '',
                trigger_values: [],
                any_trigger_value: false,
                dm_message: '',
                comment_reply: '',
                is_dm_template: false,
                dm_template: {
                  title: '',
                  subtitle: '',
                  image_url: '',
                  default_action: { type: 'web_url', url: '' },
                  buttons: [],
                },
              },
              connected_profile_details: {
                ...commonData.connected_profile_details,
                webhook_profile_id: network.webhook_profile_id,
              },
            });
            break;
          case PostUtil.ServiceName.TWITTER:
            formData.scheduled_event_requests_data.twitter_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.POST,
              cover_url: '',
            });
            break;
          case PostUtil.ServiceName.THREADS:
            formData.scheduled_event_requests_data.threads_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.POST,
            });
            break;
          case PostUtil.ServiceName.BLUESKY:
            formData.scheduled_event_requests_data.bluesky_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.POST,
            });
            break;
          case PostUtil.ServiceName.YOUTUBE:
            formData.scheduled_event_requests_data.youtube_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.VIDEO,
              thumbnail: '',
              privacy: PostUtil.YoutubePrivacyLevel.PUBLIC,
              connected_profile_details: {
                ...commonData.connected_profile_details,
                has_custom_thumbnail_permission: network.has_custom_thumbnail_permission,
              },
            });
            break;
          case PostUtil.ServiceName.LINKEDIN:
            formData.scheduled_event_requests_data.linkedin_requests_data.push({
              ...commonData,
              alert_text: formData.description,
              post_type: PostUtil.PostType.POST,
            });
            break;
          case PostUtil.ServiceName.TIKTOK:
            formData.scheduled_event_requests_data.tiktok_requests_data.push({
              ...commonData,
              title: formData.description,
              post_type: PostUtil.PostType.DIRECT_POST,
              privacy: PostUtil.TikTokPrivacyLevel.PUBLIC_TO_EVERYONE,
              allow_user_to_comment: true,
              allow_user_to_duet: true,
              allow_user_to_switch: true,
              disclosure_content: false,
              your_brand: false,
              branded_content: false,
            });
            break;
          case PostUtil.ServiceName.GBUSINESS:
            formData.scheduled_event_requests_data.gbusiness_requests_data.push({
              ...commonData,
              post_type: PostUtil.PostType.STANDARD,
              button_type: 'None',
              start_date: currentbrandTime,
              end_time: currentbrandTime,
              start_time: currentbrandTime,
              end_date: currentbrandTime.add(7, 'days'),
              connected_profile_details: {
                ...commonData.connected_profile_details,
                has_call_now_permission: network.has_custom_thumbnail_permission,
              },
            });
            break;
          case PostUtil.ServiceName.PINTEREST:
            formData.scheduled_event_requests_data.pinterest_requests_data.push({
              ...commonData,
              title: formData.description,
              board_id: '',
              cover_image_url: '',
              post_type: PostUtil.PostType.POST,
            });
            break;
          case PostUtil.ServiceName.SNAPCHAT:
            formData.scheduled_event_requests_data.snapchat_requests_data.push({
              ...commonData,
              title: formData.description,
              save_to_profile: false,
              post_type: PostUtil.PostType.PUBLIC_STORY,
            });
            break;
          default:
            break;
        }
      }
    });
    formData.brand_details = Object.values(brandsMap);
    setNewPostForm({ ...formData });
    updateRefs(formData);
  };
  const handleProfileSelectionChange = (newValue) => {
    setSelectedSocialMedias(newValue);
    handleNetworks(newValue);
  };

const handleSocialMediaChange = (newValue) => {
  const newSelectedServiceNames = newValue;

  const currentSelectedProfiles = [...selectedSocialMedias];

  const currentServiceNames = new Set(currentSelectedProfiles.map((profile) => profile.service_name));
  const newServiceNames = new Set(newSelectedServiceNames);

  const addedServices = newSelectedServiceNames.filter((service) => !currentServiceNames.has(service));
  const removedServices = [...currentServiceNames].filter((service) => !newServiceNames.has(service));

  const profilesToAdd = configuredSocialMedia.filter(
    (profile) => addedServices.includes(profile.service_name) && profile.is_active
  );

  const updatedProfiles = currentSelectedProfiles
    .filter((profile) => !removedServices.includes(profile.service_name))
    .concat(profilesToAdd);

  const uniqueProfiles = Array.from(
    new Map(updatedProfiles.map((profile) => [profile.id, profile])).values()
  );

  setSelectedSocialMedias(uniqueProfiles);
  handleNetworks(uniqueProfiles);
};

const handleBrandsChange = (newValue) => {
  const newSelectedBrands = newValue.map((brand) => brand.brand_name);
  const currentSelectedProfiles = [...selectedSocialMedias];

  const currentBrandNames = new Set(currentSelectedProfiles.map((profile) => profile.brand_name));
  const newBrandNames = new Set(newSelectedBrands);

  const addedBrands = newSelectedBrands.filter((brand) => !currentBrandNames.has(brand));
  const removedBrands = [...currentBrandNames].filter((brand) => !newBrandNames.has(brand));
  const profilesToAdd = configuredSocialMedia.filter(
    (profile) => addedBrands.includes(profile.brand_name) && profile.is_active
  );

  const updatedProfiles = currentSelectedProfiles
    .filter(
      (profile) =>
        !removedBrands.includes(profile.brand_name) || // Keep if brand not removed
        newSelectedBrands.includes(profile.brand_name)
    )
    .concat(profilesToAdd);

  const uniqueProfiles = Array.from(
    new Map(updatedProfiles.map((profile) => [profile.id, profile])).values()
  );

  setSelectedSocialMedias(uniqueProfiles);
  handleNetworks(uniqueProfiles);
};
  const updateRefs = (formData) => {
    facebookConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.facebook_requests_data);
    instagramConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.instagram_requests_data);
    twitterConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.twitter_requests_data);
    threadsConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.threads_requests_data);
    youtubeConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.youtube_requests_data);
    linkedInConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.linkedin_requests_data);
    tiktokConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.tiktok_requests_data);
    blueskyConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.bluesky_requests_data);
    snapchatConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.snapchat_requests_data);
    googleMyBusinessConditionRef?.current?.handleRequestData(
      formData.scheduled_event_requests_data.gbusiness_requests_data
    );
    pinterestConditionRef?.current?.handleRequestData(formData.scheduled_event_requests_data.pinterest_requests_data);
    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(formData)));
  };

  const handleNetworks = (socialMedias) => {
    const uniqueMedias = socialMedias.filter(
      (media, index, self) => index === self.findIndex((m) => m.id === media.id)
    );

    setSelectedSocialMedias([...uniqueMedias]);
    if (socialMedias.length === 0) {
      handleDeleteAllNetworks();
      return;
    }

    let newFormData = { ...newPostForm };
    const currentProfileIds = newFormData.connected_profile_ids;
    const newProfileIds = socialMedias.map((media) => media.id);
    const addedNetworks = socialMedias.filter((media) => !currentProfileIds.includes(media.id));
    const removedNetworks = currentProfileIds
      .filter((id) => !newProfileIds.includes(id))
      .map((id) => selectedSocialMedias.find((media) => media.id === id));

    addedNetworks.forEach((network) => {
      handleAddNewNetwork(network);
    });

    removedNetworks.forEach((network) => {
      if (network) {
        handleDeleteNetwork(network);
      }
    });
  };

  const handleAddNewNetwork = (addedNetwork) => {
    let newFormData = { ...newPostForm };

    // Add to connected profile IDs
    if (!newFormData.connected_profile_ids.includes(addedNetwork.id)) {
      newFormData.connected_profile_ids.push(addedNetwork.id);
    }

    // Find or create brand
    let brandIndex = newFormData.brand_details.findIndex((brand) => brand.brand_id === addedNetwork.brand_id);

    if (brandIndex === -1) {
      // Create new brand entry
      newFormData.brand_details.push({
        brand_id: addedNetwork.brand_id,
        connected_profiles_by_service: {
          facebook: [],
          instagram: [],
          twitter: [],
          threads: [],
          youtube: [],
          linkedin: [],
          tiktok: [],
          gbusiness: [],
          pinterest: [],
          bluesky: [],
          snapchat: [],
        },
      });
      brandIndex = newFormData.brand_details.length - 1;
    }

    // Add profile to brand
    const service = addedNetwork.service_name.toLowerCase();
    const brand = newFormData.brand_details[brandIndex];

    // Check if profile already exists in brand
    const profileExists = brand.connected_profiles_by_service[service].some(
      (profile) => profile.connected_profile_id === addedNetwork.id
    );

    if (!profileExists) {
      brand.connected_profiles_by_service[service].push({
        connected_profile_id: addedNetwork.id,
        connected_profile_name: addedNetwork.profile_name,
        connected_profile_image: addedNetwork.profile_image,
        social_media: addedNetwork.service_name,
      });
    }

    const commonData = getCommonRequestData(addedNetwork, newFormData);
    const serviceName = addedNetwork.service_name.toUpperCase();
    const serviceKey = addedNetwork.service_name.toLowerCase() + '_requests_data';
    const defaultPostType = PostUtil.getDefaultPostType(serviceName);

    // Check if a default post type already exists for this service
    const existingRequest = newFormData.scheduled_event_requests_data[serviceKey].find(
      (req) => req.post_type === defaultPostType
    );

    if (!existingRequest) {
      let requestData;
      switch (serviceName) {
        case PostUtil.ServiceName.FACEBOOK:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.POST,
            link: '',
            thumbnail: '',
            is_automation_trigger: false,
            automation_trigger: {
              trigger_type: '',
              trigger_values: [],
              any_trigger_value: false,
              dm_message: '',
              comment_reply: '',
              is_dm_template: false,
              dm_template: {
                title: '',
                subtitle: '',
                image_url: '',
                default_action: { type: 'web_url', url: '' },
                buttons: [],
              },
            },
            connected_profile_details: {
              ...commonData.connected_profile_details,
              webhook_profile_id: addedNetwork.webhook_profile_id,
            },
          };
          newFormData.scheduled_event_requests_data.facebook_requests_data.push(requestData);
          facebookConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.facebook_requests_data
          );
          break;
        case PostUtil.ServiceName.INSTAGRAM:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.POST,
            cover_url: '',
            post_on_my_wall: true,
            is_automation_trigger: false,
            automation_trigger: {
              trigger_type: '',
              trigger_values: [],
              any_trigger_value: false,
              dm_message: '',
              comment_reply: '',
              is_dm_template: false,
              dm_template: {
                title: '',
                subtitle: '',
                image_url: '',
                default_action: { type: 'web_url', url: '' },
                buttons: [],
              },
            },
            connected_profile_details: {
              ...commonData.connected_profile_details,
              webhook_profile_id: addedNetwork.webhook_profile_id,
            },
          };
          newFormData.scheduled_event_requests_data.instagram_requests_data.push(requestData);
          instagramConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.instagram_requests_data
          );
          break;
        case PostUtil.ServiceName.TWITTER:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.POST,
            cover_url: '',
          };
          newFormData.scheduled_event_requests_data.twitter_requests_data.push(requestData);
          twitterConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.twitter_requests_data
          );
          break;
        case PostUtil.ServiceName.THREADS:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.POST,
            cover_url: '',
          };
          newFormData.scheduled_event_requests_data.threads_requests_data.push(requestData);
          threadsConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.threads_requests_data
          );
          break;
        case PostUtil.ServiceName.BLUESKY:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.POST,
          };
          newFormData.scheduled_event_requests_data.bluesky_requests_data.push(requestData);
          blueskyConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.bluesky_requests_data
          );
          break;
        case PostUtil.ServiceName.YOUTUBE:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.VIDEO,
            thumbnail: '',
            privacy: PostUtil.YoutubePrivacyLevel.PUBLIC,
            connected_profile_details: {
              ...commonData.connected_profile_details,
              has_custom_thumbnail_permission: addedNetwork.has_custom_thumbnail_permission,
            },
          };
          newFormData.scheduled_event_requests_data.youtube_requests_data.push(requestData);
          youtubeConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.youtube_requests_data
          );
          break;
        case PostUtil.ServiceName.LINKEDIN:
          requestData = {
            ...commonData,
            alert_text: newFormData.description,
            post_type: PostUtil.PostType.POST,
          };
          newFormData.scheduled_event_requests_data.linkedin_requests_data.push(requestData);
          linkedInConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.linkedin_requests_data
          );
          break;
        case PostUtil.ServiceName.TIKTOK:
          requestData = {
            ...commonData,
            title: newFormData.description,
            post_type: PostUtil.PostType.DIRECT_POST,
            privacy: PostUtil.TikTokPrivacyLevel.PUBLIC_TO_EVERYONE,
            allow_user_to_comment: true,
            allow_user_to_duet: true,
            allow_user_to_switch: true,
            disclosure_content: false,
            your_brand: false,
            branded_content: false,
          };
          newFormData.scheduled_event_requests_data.tiktok_requests_data.push(requestData);
          tiktokConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.tiktok_requests_data
          );
          break;
        case PostUtil.ServiceName.GBUSINESS:
          requestData = {
            ...commonData,
            post_type: PostUtil.PostType.STANDARD,
            button_type: 'None',
            start_date: currentbrandTime,
            end_time: currentbrandTime,
            start_time: currentbrandTime,
            end_date: currentbrandTime.add(7, 'days'),
            connected_profile_details: {
              ...commonData.connected_profile_details,
              has_call_now_permission: addedNetwork.has_custom_thumbnail_permission,
            },
          };
          newFormData.scheduled_event_requests_data.gbusiness_requests_data.push(requestData);
          googleMyBusinessConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.gbusiness_requests_data
          );
          break;
        case PostUtil.ServiceName.PINTEREST:
          requestData = {
            ...commonData,
            title: newFormData.description,
            board_id: '',
            cover_image_url: '',
            post_type: PostUtil.PostType.POST,
          };
          newFormData.scheduled_event_requests_data.pinterest_requests_data.push(requestData);
          pinterestConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.pinterest_requests_data
          );
          break;
        case PostUtil.ServiceName.SNAPCHAT:
          requestData = {
            ...commonData,
            title: newFormData.description,
            save_to_profile: false,
            post_type: PostUtil.PostType.PUBLIC_STORY,
          };
          newFormData.scheduled_event_requests_data.snapchat_requests_data.push(requestData);
          snapchatConditionRef?.current?.handleRequestData(
            newFormData.scheduled_event_requests_data.snapchat_requests_data
          );
          break;
        default:
          break;
      }
    }

    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(newFormData)));
    setNewPostForm({ ...newFormData });
  };

  const handleDeleteAllNetworks = () => {
    let newFormData = { ...OrgNewPostStruct };
    newFormData.connected_profile_ids = [];
    newFormData.brand_details = [];
    Object.keys(newFormData.scheduled_event_requests_data).forEach((platform) => {
      newFormData.scheduled_event_requests_data[platform] = [];
    });
    updateRefs(newFormData);
    setNewPostForm({ ...newFormData });
  };

  const handleDeleteNetwork = (deletedNetwork) => {
    let newFormData = { ...newPostForm };
    newFormData.connected_profile_ids = newFormData.connected_profile_ids.filter((id) => id !== deletedNetwork.id);

    // Find brand
    const brandIndex = newFormData.brand_details.findIndex((brand) => brand.brand_id === deletedNetwork.brand_id);

    if (brandIndex !== -1) {
      const brand = newFormData.brand_details[brandIndex];
      const service = deletedNetwork.service_name.toLowerCase();

      // Remove profile from brand
      brand.connected_profiles_by_service[service] = brand.connected_profiles_by_service[service].filter(
        (profile) => profile.connected_profile_id !== deletedNetwork.id
      );

      // Check if brand is empty
      const isBrandEmpty = Object.values(brand.connected_profiles_by_service).every(
        (profiles) => profiles.length === 0
      );

      if (isBrandEmpty) {
        // Remove brand if empty
        newFormData.brand_details.splice(brandIndex, 1);
      }
    }

    // Clear scheduled_event_requests_data if service has no more profiles
    const serviceKey = deletedNetwork.service_name.toLowerCase() + '_requests_data';
    const hasProfilesForService = newFormData.brand_details.some(
      (brand) => brand.connected_profiles_by_service[deletedNetwork.service_name.toLowerCase()]?.length > 0
    );

    if (!hasProfilesForService) {
      newFormData.scheduled_event_requests_data[serviceKey] = [];
    }

    // Update service-specific refs
    switch (deletedNetwork.service_name.toUpperCase()) {
      case PostUtil.ServiceName.FACEBOOK:
        facebookConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.facebook_requests_data
        );
        break;
      case PostUtil.ServiceName.INSTAGRAM:
        instagramConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.instagram_requests_data
        );
        break;
      case PostUtil.ServiceName.TWITTER:
        twitterConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.twitter_requests_data
        );
        break;
      case PostUtil.ServiceName.THREADS:
        threadsConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.threads_requests_data
        );
        break;
      case PostUtil.ServiceName.BLUESKY:
        blueskyConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.bluesky_requests_data
        );
        break;
      case PostUtil.ServiceName.SNAPCHAT:
        snapchatConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.snapchat_requests_data
        );
        break;
      case PostUtil.ServiceName.YOUTUBE:
        youtubeConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.youtube_requests_data
        );
        break;
      case PostUtil.ServiceName.LINKEDIN:
        linkedInConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.linkedin_requests_data
        );
        break;
      case PostUtil.ServiceName.TIKTOK:
        tiktokConditionRef?.current?.handleRequestData(newFormData.scheduled_event_requests_data.tiktok_requests_data);
        break;
      case PostUtil.ServiceName.GBUSINESS:
        googleMyBusinessConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.gbusiness_requests_data
        );
        break;
      case PostUtil.ServiceName.PINTEREST:
        pinterestConditionRef?.current?.handleRequestData(
          newFormData.scheduled_event_requests_data.pinterest_requests_data
        );
        break;
      default:
        break;
    }

    previewRef?.current?.handlePreviewData(JSON.parse(JSON.stringify(newFormData)));
    setNewPostForm({ ...newFormData });
  };

  const handleAddHashTags = (content) => {
    setHashTags(content);
  };

  const handleAddAllDescription = (description) => {
    let newFormData = { ...newPostForm };
    newFormData.description = description;

    Object.keys(newFormData.scheduled_event_requests_data).forEach((platform) => {
      newFormData.scheduled_event_requests_data[platform].forEach((request) => {
        request.description = description;
        if (request.alert_text !== undefined) {
          request.alert_text = description;
        }
        if (request.title !== undefined) {
          request.title = description;
        }
      });
    });

    updateRefs(newFormData);
    setNewPostForm(newFormData);
  };

  const handleAddAllCommentDescription = (commentDescription) => {
    let newFormData = { ...newPostForm };
    newFormData.comment_description = commentDescription;
    newFormData.has_comment = commentDescription !== '' ? true : false;

    Object.keys(newFormData.scheduled_event_requests_data).forEach((platform) => {
      newFormData.scheduled_event_requests_data[platform].forEach((request) => {
        request.comment_description = commentDescription;
        request.has_comment = commentDescription !== '' ? true : false;
      });
    });

    updateRefs(newFormData);
    setNewPostForm(newFormData);
  };

  const handleMediaFiles = (mediaFiles) => {
    let newFormData = { ...newPostForm };
    newFormData.media_url = mediaFiles.map((a) => a.media_url).join();
    newFormData.media_data = mediaFiles;

    Object.keys(newFormData.scheduled_event_requests_data).forEach((platform) => {
      newFormData.scheduled_event_requests_data[platform].forEach((request) => {
        request.media_url = newFormData.media_url;
        request.media_data = mediaFiles;
      });
    });

    updateRefs(newFormData);
    setNewPostForm(newFormData);
  };

  const handleCloseReauthenticate = () => {
    setReAuthenticateOpen(false);
    setSelectedPopOverAccount(null);
  };

  const handleProfileClick = (profile) => {
    setSelectedPopOverAccount(profile);
    setReAuthenticateOpen(true);
  };

  const updateRequestData = (data, serviceName) => {
    let newFormData = { ...newPostForm };
    newFormData.scheduled_event_requests_data[serviceName.toLowerCase() + '_requests_data'] = [...data];
    setNewPostForm({ ...newFormData });
    updateRefs(newFormData);
  };


  const saveDraftSuccessCallback = (data, message) => {
    message && showToast(message, 's');
    setDraftSubmitting(false);
  };

  const handleSaveDraft = () => {
    setDraftSubmitting(true);
    let bodyprops = getFormDataProps();
    let postProps = {
      url: postsModuleURL.orgSaveDraft,
      body: bodyprops,
      successCallback: saveDraftSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const getFormDataProps = () => {
    let postDateTime = CommonHelper.getBrandTimeZoneTime();
    let isScheduledPost = false;

    if (whenToPost === PostOption.SchedulePost.Name || whenToPost === PostOption.ApprovalPost.Name) {
      postDateTime = CommonHelper.getBrandTimeZoneTime(scheduleDateTime);
      isScheduledPost = true;
    }

    const mediaUrls = newPostForm.media_data.map((file) => file.media_url);

    const now = CommonHelper.getBrandTimeZoneTime();
    const normalizeDateTime = (date) => date.set('second', 0).set('millisecond', 0);

    const normalizedPostDateTime = normalizeDateTime(postDateTime);
    const normalizedNow = normalizeDateTime(now);

    const adjustedPostDateTime =
      whenToPost === PostOption.PostNow.Name
        ? now
        : !hasApproverPermission && normalizedPostDateTime.isSame(normalizedNow)
        ? normalizedPostDateTime.add(5, 'minute')
        : normalizedPostDateTime;

    // Process brand_details to merge duplicates
    const brandMap = {};
    newPostForm.brand_details.forEach((brand) => {
      const brandId = parseInt(brand.brand_id, 10);
      if (!brandMap[brandId]) {
        brandMap[brandId] = {
          brand_id: brandId,
          connected_profiles_by_service: {
            facebook: [],
            instagram: [],
            twitter: [],
            threads: [],
            youtube: [],
            linkedin: [],
            tiktok: [],
            gbusiness: [],
            pinterest: [],
            bluesky: [],
            snapchat: [],
          },
        };
      }

      // Deduplicate profiles within services
      Object.entries(brand.connected_profiles_by_service).forEach(([service, profiles]) => {
        const serviceKey = service.toLowerCase();
        if (!brandMap[brandId].connected_profiles_by_service[serviceKey]) return;

        const existingIds = new Set(
          brandMap[brandId].connected_profiles_by_service[serviceKey].map((p) => p.connected_profile_id)
        );

        profiles.forEach((profile) => {
          if (!existingIds.has(profile.connected_profile_id)) {
            brandMap[brandId].connected_profiles_by_service[serviceKey].push(profile);
            existingIds.add(profile.connected_profile_id);
          }
        });
      });
    });

    const cleanedBrandDetails = Object.values(brandMap);

    const formbody = {
      scheduled_time: adjustedPostDateTime.toDate(),
      description: newPostForm.description,
      comment_description: newPostForm.comment_description,
      media_url: mediaUrls,
      is_file_upload: true,
      is_scheduled_post: isScheduledPost,
      schedule_id: scheduleId ? scheduleId.toString() : undefined,
      is_approval: whenToPost === PostOption.ApprovalPost.Name,
      is_org_new_post: true,
      scheduled_event_requests_data: newPostForm.scheduled_event_requests_data,
      brand_details: cleanedBrandDetails, // Use merged brand details
    };

    return scheduleId
      ? {
          schedule_id: scheduleId,
          new_post_datas: formbody,
        }
      : formbody;
  };

  const handleScheduleDate = (date) => {
    if (date) {
      const convertedDate = CommonHelper.getBrandTimeZoneTime(date);
      setScheduleDateTime(convertedDate);
    }
  };

  const handlePostTypeChange = (event) => {
    let isChecked = event.target.checked;
    let postOption = isChecked ? PostOption.SchedulePost.Name : PostOption.PostNow.Name;
    setWhenToPost(postOption);
  };

  const handleSelectPostTypeChange = (value) => {
    setWhenToPost(value);
  };

  const newPostSuccessCallback = (data, message) => {
    showToast(message, 's');
    setLoader(false);
    setPostSubmitting(false);
    handleDeleteAllNetworks();
    setStateToInitialStage();
  };

  const handleFormsubmit = async () => {
    if (selectedSocialMedias && selectedSocialMedias.length > 0) {
      setTabValue(1);
      await new Promise((resolve) => setTimeout(resolve, 100));
      let isValidForm = checkFormIsValid();
      if (isValidForm) {
        setPostSubmitting(true);
        let formBody = getFormDataProps();
        let postProps = {
          url: scheduleId ? postsModuleURL.updateOrgScheduledPost : postsModuleURL.uploadOrgPosts,
          body: formBody,
          successCallback: newPostSuccessCallback,
          failureCallback,
        };
        HttpServices.Post(postProps);
      } else {
        showToast('Please fix all the validations to post', 'e');
      }
    } else {
      showToast('Please fix all the validations to post', 'e');
    }
  };

  const checkFormIsValid = () => {
    let errorCount = 0;
    const { scheduled_event_requests_data } = newPostForm;
    const validateServiceData = (serviceData) => {
      serviceData.forEach((data) => {
        if (data.errors && data.errors.length > 0) {
          errorCount += data.errors.length;
        }
      });
    };

    const refMap = {
      facebook: facebookConditionRef,
      instagram: instagramConditionRef,
      twitter: twitterConditionRef,
      threads: threadsConditionRef,
      youtube: youtubeConditionRef,
      linkedin: linkedInConditionRef,
      tiktok: tiktokConditionRef,
      gbusiness: googleMyBusinessConditionRef,
      pinterest: pinterestConditionRef,
      bluesky: blueskyConditionRef,
      snapchat: snapchatConditionRef,
    };

    Object.keys(scheduled_event_requests_data).forEach((platform) => {
      const service = platform.split('_')[0];
      if (scheduled_event_requests_data[platform].length > 0 && refMap[service]?.current) {
        refMap[service].current.checkForErrors();
        validateServiceData(scheduled_event_requests_data[platform]);
      }
    });

    return errorCount === 0;
  };

  return (
    <>
      <Helmet>
        <title>Create New Post</title>
      </Helmet>
      {loader ? (
        <Spinner />
      ) : isMobile ? (
        <MobileNewPostComponent
          configuredSocialMedia={configuredSocialMedia}
          selectedSocialMedias={selectedSocialMedias}
          handleNetworks={handleNetworks}
          newPostForm={newPostForm}
          handleAddAllDescription={handleAddAllDescription}
          handleAddAllCommentDescription={handleAddAllCommentDescription}
          handleMediaFiles={handleMediaFiles}
          tabValue={tabValue}
          setTabValue={setTabValue}
          updateRequestData={updateRequestData}
          googleMyBusinessConditionRef={googleMyBusinessConditionRef}
          tiktokConditionRef={tiktokConditionRef}
          linkedInConditionRef={linkedInConditionRef}
          youtubeConditionRef={youtubeConditionRef}
          twitterConditionRef={twitterConditionRef}
          threadsConditionRef={threadsConditionRef}
          instagramConditionRef={instagramConditionRef}
          facebookConditionRef={facebookConditionRef}
          pinterestConditionRef={pinterestConditionRef}
          blueskyConditionRef={blueskyConditionRef}
          snapchatConditionRef={snapchatConditionRef}
          previewRef={previewRef}
          handleSaveDraft={handleSaveDraft}
          isSubmittingDraft={isSubmittingDraft}
          handleScheduleDate={handleScheduleDate}
          scheduleDateTime={scheduleDateTime}
          whenToPost={whenToPost}
          PostOption={PostOption}
          handlePostTypeChange={handlePostTypeChange}
          isSubmitting={isSubmitting}
          handleFormsubmit={handleFormsubmit}
          hasApproverPermission={hasApproverPermission}
          handleSelectPostTypeChange={handleSelectPostTypeChange}
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} style={{ paddingBottom: '12px' }}>
            <PostContentComponent
              configuredSocialMedia={configuredSocialMedia}
              handleProfileClick={handleProfileClick}
              selectedSocialMedias={selectedSocialMedias}
              isOrgConnectedProfilesData={true}
              handleNetworks={handleNetworks}
              newPostForm={newPostForm}
              handleAddAllDescription={handleAddAllDescription}
              handleAddAllCommentDescription={handleAddAllCommentDescription}
              handleMediaFiles={handleMediaFiles}
              hashTags={hashTags}
              handleAddHashTags={handleAddHashTags}
              onProfileSelectionChange={handleProfileSelectionChange}
              onSocialMediaChange={handleSocialMediaChange}
              onBrandsChange={handleBrandsChange}
              isShowOfferBanner={isShowOfferBanner}
            />
          </Grid>
          <Grid item xs={12} md={6} style={{ paddingBottom: '12px' }}>
            <PostTabComponent
              tabValue={tabValue}
              setTabValue={setTabValue}
              isOrgConnectedProfilesOpen={true}
              updateRequestData={updateRequestData}
              googleMyBusinessConditionRef={googleMyBusinessConditionRef}
              tiktokConditionRef={tiktokConditionRef}
              linkedInConditionRef={linkedInConditionRef}
              youtubeConditionRef={youtubeConditionRef}
              twitterConditionRef={twitterConditionRef}
              threadsConditionRef={threadsConditionRef}
              instagramConditionRef={instagramConditionRef}
              facebookConditionRef={facebookConditionRef}
              pinterestConditionRef={pinterestConditionRef}
              blueskyConditionRef={blueskyConditionRef}
              snapchatConditionRef={snapchatConditionRef}
              previewRef={previewRef}
              newPostForm={newPostForm}
              isOrgConnectedProfilesData={true}
              isShowOfferBanner={isShowOfferBanner}
            />
          </Grid>
          <PostFooter
            handleSaveDraft={handleSaveDraft}
            isSubmittingDraft={isSubmittingDraft}
            handleScheduleDate={handleScheduleDate}
            scheduleDateTime={scheduleDateTime}
            whenToPost={whenToPost}
            PostOption={PostOption}
            hasApproverPermission={hasApproverPermission}
            handlePostTypeChange={handlePostTypeChange}
            handleSelectPostTypeChange={handleSelectPostTypeChange}
            isSubmitting={isSubmitting}
            handleFormsubmit={handleFormsubmit}
          />
        </Grid>
      )}
      {reAuthenticateOpen && (
        <CommonReAuthenticateComponent
          selectedPopOverAccount={selectedPopOverAccount}
          getConnectedNetworks={getOrgConnectedNetworks}
          onClose={handleCloseReauthenticate}
        />
      )}
    </>
  );
};

export default OrgNewPostComponent;
